// no-paste-chrome — refuse paste, cut and text-drop into the code editor and graded answer
// fields when the student's Class has pasting turned off (class-block-paste, Trello l3TPxPs3).
//
// ── WHY IT IS WIRED EAGERLY ────────────────────────────────────────────────────────────────
// Wired UNIFORMLY from main.js — next to wireReadAloud / wireTimeOnTask / wireActivityTour,
// ABOVE the crossOriginIsolated early-return and OUTSIDE loadProject().then(). It is NOT a
// `SURFACES` entry: that map is per-surface-exclusive and this spans seven of them.
//
// It can be eager because it needs neither the project payload nor `core.getEditor()`. Its only
// inputs are the SERVER-RENDERED `<body>` attributes and the surface's own `#<surface>-config`
// JSON block, and it listens at `document` rather than binding to elements. That last part is
// load-bearing twice over: the graded answer fields on `predict` and `completion` are rendered
// by their own chrome modules AFTER this one wires, so a wire-time querySelector would find
// nothing; and delegation means a refusal decision is made at EVENT time against whatever is
// on the page then. Do not "optimise" this into per-element listeners.
//
// ── WHY `stopImmediatePropagation()` IS REQUIRED ───────────────────────────────────────────
// `preventDefault()` alone DOES NOT STOP ACE. Ace registers its own `paste` and `cut` listeners
// on its hidden textarea (`ace.js:2194`) and `TextInput.prototype.onPaste` reads
// `e.clipboardData` itself and calls `session.insert` — `preventDefault()` cancels only the
// BROWSER's default action, not a deeper JS listener. Without `stopImmediatePropagation()` the
// block silently does nothing on `assignment`, `challenge`, `debug` and `guided-make` — the four
// surfaces where `#editor` IS the graded answer — while appearing to work on `predict` and
// `completion`, which use real form controls. Every automated gate would pass. A source-text
// spec pins the call; do not remove it.
//
// ── WHY THE ALLOW-LIST IS LOAD-BEARING ─────────────────────────────────────────────────────
// Refusal is allow-listed BY SURFACE, never inferred from the presence of a server flag. Wired
// uniformly this module would otherwise also run on `review` / `challengeFeedback` (both TEACHER
// surfaces), on `viewonly`, on `trace-capture`, and on the DEFAULT `editor` surface, which covers
// the plain project editor and every standalone pgzero page — none of which belongs to a class
// whose setting could be read. Having a server-resolved flag does not exempt a module from the
// allow-list (`time-on-task-chrome.js` carries one too and is still on main.js's uniform list).
//
// ── WHAT IS AND IS NOT REFUSED ─────────────────────────────────────────────────────────────
// Refused: the Ace mount (`#editor` and the hidden textarea inside it), `.predict-textarea`,
// `.completion-blank`, and compare's `.compare-reason` judge-reason textareas (compare-activity —
// graded-style free text; they get their own message, "Type your own reason in.").
// NOT refused, deliberately:
//   * COPY. Students must still be able to copy their own code out to a teacher or a help
//     request. Only paste and cut are refused (cut because, with paste blocked, a cut destroys
//     work the student cannot restore — owner's decision).
//   * `tweak`'s `.tweak-w` parameter chips. A tweak student never types code; pasting a number
//     into a number chip is not the behaviour this exists to stop. The surface stays allow-listed
//     and stays plumbed so a future tweak variant with a text field is covered — but in practice
//     it refuses nothing today, which makes `tweak` the wrong surface to test this on.
//   * The feedback widget, the assets panel, and any search / filename input.
//   * `.runner-stdin-input` — that is input to the student's PROGRAM, not a graded answer.
//   * Anything inside `#runner-frame`. Frame events never bubble out to this document, and
//     reaching into the frame would force late wiring and destroy the eager design above. This
//     is a documented boundary, not a missed target.
//
// ── WHAT THIS MODULE DOES NOT NEED ─────────────────────────────────────────────────────────
// No `core.isLoading()` guard. Unlike `authenticity-capture.js` — which hooks ACE's own `paste`
// event and therefore does need one — this listens for DOM clipboard and drag events. Initial
// load, draft rehydrate, restore, starter code, carry-forward from a previous session and buffer
// rebuilds are all direct buffer writes and fire none of those events. Adding the guard would
// also force a `core` reference and destroy the eager wiring.
//
// It never touches editor-core or editor-api, changes no postMessage contract, and leaves
// `authenticity-capture.js` alone: with the block ON no paste occurs, so detection has nothing
// to record; with it OFF this module is inert and detection is unchanged.
//
// KNOWN LIMIT: Ace's TOUCH context menu pastes via `navigator.clipboard.readText()` and fires no
// DOM event at all, so on iPads and touchscreen Chromebooks the block is a no-op on the code
// editor. Suppressing that menu was rejected as keep-list-adjacent risk. Accepted and documented.

// The nine isolated surfaces a class-enrolled student genuinely works on. An explicit local
// list on purpose — never derived from another module's export.
var BLOCKING_SURFACES = [
  'assignment',
  'challenge',
  'predict',
  'debug',
  'completion',
  'guided-make',
  'tweak',
  // find-the-bug-activity: `#editor` becomes the student's answer once the bug is found, and the
  // flag rides its `#find-the-bug-config` block like debug's.
  'find-the-bug',
  // compare-activity (Trello m98B2mq7): the four judge-reason textareas (`.compare-reason`). The
  // flag rides its `#compare-config` block (`noPaste`), so BODY_ATTR_SURFACES is unchanged.
  'compare'
];

// The two surfaces that publish the flag as a <body> data attribute. Every other surface on the
// allow-list reads it from its `#<surface>-config` JSON block — `guided-make` included, which
// carries body attributes too but whose config is the authority.
var BODY_ATTR_SURFACES = ['assignment', 'challenge'];

// Evaluated at EVENT time against `event.target.closest()`, never bound at wire time.
var REFUSAL_SELECTOR = '#editor, .ace_editor, .predict-textarea, .completion-blank, .compare-reason';

var PASTE_MESSAGE = 'Pasting is turned off for this class. Type your code in.';
// compare-activity: a per-selector message — a reason is not code. `.compare-reason` is unique to
// the compare surface, so no other surface's copy changes.
var REASON_SELECTOR = '.compare-reason';
var REASON_PASTE_MESSAGE = 'Pasting is turned off for this class. Type your own reason in.';
var CUT_MESSAGE   = "Cut is off as well, so you can't lose work you can't paste back. Copy still works.";

var TOAST_MS    = 5000;
// A refusal within this window restarts the visible timer but does NOT re-announce. Holding
// Ctrl-V fires `paste` on keyboard auto-repeat, and a clear-then-write per refusal would
// machine-gun a screen-reader user several times a second.
var THROTTLE_MS = 1500;

var STYLES = [
  // A small self-dismissing toast — deliberately NOT a modal. `close-prompt.js` is the pattern
  // precedent (own DOM, token-only styling, one announcer) but it builds a scrim'd
  // `aria-modal` dialog, and a focus-trapping modal every time a student presses Ctrl-V would be
  // far more disruptive than the paste it refuses. No scrim, no dialog role, no close button,
  // not tabbable, never moves focus, no Escape handler (Escape is already claimed capture-phase
  // by close-prompt, the assets/settings modals and editor-core).
  //
  // Bottom-LEFT because bottom-right is `pc-feedback` (z-index 900). z-index 950 sits above it
  // and below close-prompt's success scrim (1000). Not "near the editor": `#editor` is
  // display:none on `completion` and `tweak`, and on `predict` the refusable field is in the
  // right rail, so "near the editor" resolves to nothing on three of the seven surfaces.
  '.pcnp-toast{position:fixed;left:var(--space-4,1rem);bottom:var(--space-4,1rem);z-index:950;',
  '  max-width:22rem;box-sizing:border-box;',
  '  padding:var(--space-3,0.75rem) var(--space-4,1rem);',
  '  border:1px solid var(--color-warning);border-radius:var(--radius-md,12px);',
  // The DS warning pairing: soft amber fill with the darkened amber-700 foreground, which is the
  // shade that clears WCAG AA on it (~5.2:1). NEVER `--color-warning-strong` — it is referenced
  // in three places in this repo and DEFINED NOWHERE, so it silently paints inherited ink.
  '  background:var(--color-warning-soft);color:var(--amber-700);',
  '  font-family:var(--font-sans,system-ui,sans-serif);font-size:var(--fs-sm,0.875rem);',
  '  line-height:var(--lh-normal,1.5);font-weight:600;',
  '  box-shadow:var(--shadow-lg);',
  '  animation:pcnp-rise var(--dur-base,180ms) var(--ease-out,ease-out);}',
  '.pcnp-toast[hidden]{display:none;}',
  '@keyframes pcnp-rise{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}',
  '@media (prefers-reduced-motion: reduce){.pcnp-toast{animation:none;}}',
  '.pcnp-sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;',
  '  clip:rect(0 0 0 0);white-space:nowrap;border:0;}'
].join('\n');

// ── module state (one wiring per page) ─────────────────────────────────────────────────────
var wired      = false;
var toastEl    = null;
var announcer  = null;
var hideTimer  = null;
var lastNotice = 0;
// True while a drag that STARTED inside a refusal target is in flight. Ace's drop handler serves
// both an external text drop and an internal selection move, distinguished by a private flag a
// document-level listener cannot see; refusing every drop would break drag-to-move and send Ace
// down its `onDragEnd` `dropEffect == "move"` path, deleting the dragged text with nowhere to
// put it.
var internalDrag = false;

function ensureStyles(doc) {
  if (doc.getElementById('pcnp-styles')) { return; }
  var el = doc.createElement('style');
  el.id = 'pcnp-styles';
  el.textContent = STYLES;
  (doc.head || doc.documentElement).appendChild(el);
}

// Both nodes are built ONCE at wire time. A `role="status"` element created and written in the
// same tick does not reliably announce, and building a fresh announcer per refusal would hit
// exactly that. The visible toast is `aria-hidden` so the message is never heard twice.
function buildNotice(doc) {
  ensureStyles(doc);

  toastEl = doc.createElement('div');
  toastEl.className = 'pcnp-toast';
  toastEl.setAttribute('aria-hidden', 'true');
  toastEl.hidden = true;

  announcer = doc.createElement('span');
  announcer.className = 'pcnp-sr';
  announcer.setAttribute('role', 'status');

  doc.body.appendChild(toastEl);
  doc.body.appendChild(announcer);
}

function showNotice(doc, message) {
  if (!toastEl) { return; }
  var now = Date.now();
  var quiet = (now - lastNotice) >= THROTTLE_MS;
  lastNotice = now;

  toastEl.textContent = message;
  toastEl.hidden = false;

  // Clear-then-write so an identical repeat message still announces — but only outside the
  // throttle window. Inside it the visible timer restarts silently.
  if (quiet && announcer) {
    announcer.textContent = '';
    announcer.textContent = message;
  }

  if (hideTimer) { clearTimeout(hideTimer); }
  hideTimer = setTimeout(function () {
    hideTimer = null;
    if (toastEl) { toastEl.hidden = true; }
    if (announcer) { announcer.textContent = ''; }
  }, TOAST_MS);
}

// Is this event aimed at a field the student's answer is typed into?
function isRefusalTarget(target) {
  if (!target || typeof target.closest !== 'function') { return false; }
  try {
    return !!target.closest(REFUSAL_SELECTOR);
  } catch (e) {
    return false;
  }
}

// The paste/drop refusal copy for a target: the reason fields get their own words.
function pasteMessageFor(target) {
  try {
    if (target && typeof target.closest === 'function' && target.closest(REASON_SELECTOR)) {
      return REASON_PASTE_MESSAGE;
    }
  } catch (e) { /* fall through to the code message */ }
  return PASTE_MESSAGE;
}

// Would this paste actually insert anything? An empty clipboard passes through silently with no
// notice. When the clipboard cannot be inspected at all (a browser that withholds
// `clipboardData` on the event) we treat it as carrying text and refuse — the no-op exemption is
// a nicety, and failing it open would quietly disable the feature on that browser.
function pasteCarriesText(ev) {
  var dt = ev.clipboardData || (typeof window !== 'undefined' && window.clipboardData);
  if (!dt || typeof dt.getData !== 'function') { return true; }
  var text;
  try {
    text = dt.getData('text/plain') || dt.getData('Text') || '';
  } catch (e) {
    return true;
  }
  return text.length > 0;
}

// A FILE drop keeps today's browser behaviour untouched; only a text drop is refused.
function dropCarriesText(ev) {
  var dt = ev.dataTransfer;
  if (!dt) { return false; }
  var types = dt.types;
  if (!types) { return false; }
  for (var i = 0; i < types.length; i++) {
    if (types[i] === 'text/plain' || types[i] === 'Text' || types[i] === 'text') { return true; }
  }
  return false;
}

// preventDefault() cancels the browser default; stopImmediatePropagation() is what actually stops
// Ace, which reads the clipboard from its own textarea listener. BOTH are required — see the
// header. Applies to `cut` exactly as it does to `paste`.
function refuse(ev, doc, message) {
  ev.preventDefault();
  ev.stopImmediatePropagation();
  showNotice(doc, message);
}

/**
 * EAGER entry point, wired from main.js above the crossOriginIsolated early-return and outside
 * loadProject().then(). Null-safe and throw-safe: it runs inside main.js's IIFE, where a throw
 * would skip every later wiring step.
 *
 * @param {Document} doc
 */
export function wireNoPaste(doc) {
  try {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!doc || !doc.body || wired) { return; }

    var surface = doc.body.getAttribute('data-surface') || 'editor';
    if (BLOCKING_SURFACES.indexOf(surface) === -1) { return; }

    if (!readBlocked(doc, surface)) { return; }

    wired = true;
    buildNotice(doc);

    doc.addEventListener('paste', function (ev) {
      if (!isRefusalTarget(ev.target)) { return; }
      if (!pasteCarriesText(ev)) { return; }
      refuse(ev, doc, pasteMessageFor(ev.target));
    }, true);

    doc.addEventListener('cut', function (ev) {
      if (!isRefusalTarget(ev.target)) { return; }
      refuse(ev, doc, CUT_MESSAGE);
    }, true);

    // COPY IS NOT LISTENED FOR AT ALL. Students must keep being able to copy their own code out.

    doc.addEventListener('dragstart', function (ev) {
      internalDrag = isRefusalTarget(ev.target);
    }, true);

    doc.addEventListener('dragend', function () {
      internalDrag = false;
    }, true);

    doc.addEventListener('drop', function (ev) {
      // READ the flag before clearing it. Clearing at the top of the handler would make it always
      // false at decision time, which refuses EVERY internal drag-to-move — the precise behaviour
      // the flag exists to preserve. It is cleared here as well as in `dragend` only because a
      // drag can end without ever firing `dragend` (an OS interrupt, an alt-tab mid-drag, a
      // cancelled drag), which would otherwise leave it stuck true and let one later external
      // drop through unrefused.
      var wasInternal = internalDrag;
      internalDrag = false;
      if (wasInternal) { return; }
      if (!isRefusalTarget(ev.target)) { return; }
      if (!dropCarriesText(ev)) { return; }
      refuse(ev, doc, pasteMessageFor(ev.target));
    }, true);
  }
  catch (e) {
    // Never take the rest of main.js's eager wiring down.
  }
}

// Absent, missing or unparseable ⇒ NOT blocked, consistent with the server resolver's fail-open.
function readBlocked(doc, surface) {
  if (BODY_ATTR_SURFACES.indexOf(surface) !== -1) {
    // A STRING comparison: the attribute is emitted only when true, so `!== 'false'` would read
    // an absent attribute as blocked.
    return doc.body.getAttribute('data-no-paste') === 'true';
  }
  var el = doc.getElementById(surface + '-config');
  if (!el) { return false; }
  var cfg;
  try {
    cfg = JSON.parse(el.textContent || '{}');
  } catch (e) {
    return false;
  }
  return !!cfg && cfg.noPaste === true;
}
